import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ClipboardCheck, Gauge, Plug, TrendingUp, Loader2 } from "lucide-react";
import { AnimatedSection, AnimatedItem } from "@/hooks/useScrollAnimation";
import { useStaticTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { track } from "@/lib/analytics";
import { submitLead } from "@/lib/submitLead";

const highlights = [
  { icon: ClipboardCheck, text: "Factory Visibility Assessment with your team" },
  { icon: Gauge, text: "Baseline OEE, downtime and energy losses" },
  { icon: Plug, text: "ERP / APS / IIoT integration architecture" },
  { icon: TrendingUp, text: "Practical roadmap: start small, scale big" },
];

const industryOptions = [
  "Cable & Wire Manufacturing",
  "Discrete Manufacturing",
  "Switchgear & Electrical Panels",
  "Metal Processing & Fabrication",
  "Plastics & Extrusion",
  "Food & Packaging",
  "Custom Manufacturing",
  "Other",
];

const goalOptions = [
  "Increase output",
  "Reduce downtime",
  "Improve OEE",
  "Digitize job orders",
  "Control material flow",
  "QR traceability",
  "Improve quality",
  "Maintenance / CMMS",
  "Energy management",
  "ERP integration",
];

/** Pre-filled message for CTAs that link here with ?topic= (request type is unchanged). */
const TOPIC_MESSAGES: Record<string, string> = {
  "tools-management": "I would like a demo of BEEDIGIT Tools Management.",
  "copper-savings": "I would like to discuss our copper savings potential through drawing die management.",
  "copper-research-paper": "Please send me the BHAG Digital technical research paper on copper-efficient wire drawing die management.",
};

const Contact = ({ id, asPageTitle = false }: { id?: string; asPageTitle?: boolean }) => {
  const HeadingTag = (asPageTitle ? "h1" : "h2") as "h1" | "h2";
  const [searchParams, setSearchParams] = useSearchParams();
  const requestParam = searchParams.get("request");
  const isStarter = requestParam === "starter-package";
  const requestType = isStarter ? "starter_package" : "factory_assessment";
  const { t } = useStaticTranslation();
  const heading = t(isStarter
    ? "Request the BEEDIGIT Starter Package"
    : "Book a Factory Visibility Assessment");
  const subheading = t(isStarter
    ? "Preconfigured Plug & Play kit to get one work center live in weeks. Tell us about your factory and we'll confirm scope, hardware and timeline."
    : "Start with one line, one bottleneck or one factory. BEEDIGIT will help you identify losses, increase output and build a practical digital manufacturing roadmap.");
  const eyebrowText = t(isStarter
    ? "You're requesting the BEEDIGIT Starter Package"
    : "Ready to make your factory visible?");
  const submitLabel = t(isStarter ? "Request Starter Package" : "Book Factory Visibility Assessment");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [industry, setIndustry] = useState("");
  const [machines, setMachines] = useState("");
  const [erp, setErp] = useState("");
  const [goals, setGoals] = useState<string[]>([]);
  const [message, setMessage] = useState(() => TOPIC_MESSAGES[searchParams.get("topic") ?? ""] ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => nameRef.current?.focus(), 400);
  };

  const requestStarter = () => {
    const next = new URLSearchParams(searchParams);
    next.set("request", "starter-package");
    setSearchParams(next, { replace: false });
    scrollToForm();
  };

  useEffect(() => {
    if (isStarter) {
      // Focus form when arriving with query param
      setTimeout(() => scrollToForm(), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStarter]);

  const toggleGoal = (g: string) =>
    setGoals((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = `${firstName} ${lastName}`.trim();
    if (!name || !email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await submitLead({
        request_type: requestType,
        name,
        company: company.trim() || null,
        email: email.trim(),
        phone: phone.trim() || null,
        country_city: country.trim() || null,
        industry: industry || null,
        plants: machines.trim() || null,
        erp_system: erp.trim() || null,
        goals: goals.length > 0 ? goals : null,
        message: message.trim() || null,
        website: honeypot,
      });
      track(
        isStarter ? "form_starter_package_submit" : "form_factory_assessment_submit",
        { industry, goals: goals.join(",") }
      );
      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setCompany("");
      setCountry(""); setIndustry(""); setMachines(""); setErp(""); setGoals([]); setMessage("");
      toast({ title: "✅ " + t("Request submitted"), description: t("We'll suggest the fastest starting point for your factory."), duration: 6000 });
    } catch {
      toast({ title: t("Error"), description: t("Something went wrong. Please try again."), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id={id} className="py-20 border-t border-border bg-[hsl(var(--section-alt))]">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-10">
            <p className="eyebrow mb-3 justify-center">{t(eyebrowText)}</p>
            <HeadingTag className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              {t(heading)}
            </HeadingTag>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t(subheading)}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-[0.9fr_1.1fr] gap-8">
            <AnimatedItem delay={0.1}>
              <div className="space-y-6">
                <ul className="space-y-4">
                  {highlights.map((h) => {
                    const Icon = h.icon;
                    return (
                      <li key={h.text} className="flex items-start gap-3">
                        <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground/90">{t(h.text)}</span>
                      </li>
                    );
                  })}
                </ul>
                <div className="rounded-lg border border-border bg-card p-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t("Prefer to explore first?")}</p>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant={isStarter ? "default" : "outline"}
                      size="sm"
                      onClick={requestStarter}
                      aria-pressed={isStarter}
                    >
                      {t("Request BEEDIGIT Starter Package")}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("No obligation. We will suggest the fastest starting point for your factory.")}
                </p>
              </div>
            </AnimatedItem>

            <AnimatedItem delay={0.2}>
              <div ref={formRef} className="border border-border bg-card p-6 md:p-8 rounded-lg">
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden"><label htmlFor="contact-website">Leave this field empty</label><input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="contact-first-name" className="block text-xs font-medium mb-1.5">{t("First name*")}</label>
                      <Input id="contact-first-name" name="firstName" autoComplete="given-name" ref={nameRef} required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor="contact-last-name" className="block text-xs font-medium mb-1.5">{t("Last name*")}</label>
                      <Input id="contact-last-name" name="lastName" autoComplete="family-name" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium mb-1.5">{t("Work email*")}</label>
                      <Input id="contact-email" name="email" autoComplete="email" type="email" required placeholder={t("email@company.com")} value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-xs font-medium mb-1.5">{t("Phone / WhatsApp")}</label>
                      <Input id="contact-phone" name="phone" autoComplete="tel" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="contact-company" className="block text-xs font-medium mb-1.5">{t("Company*")}</label>
                      <Input id="contact-company" name="company" autoComplete="organization" required value={company} onChange={(e) => setCompany(e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor="contact-country" className="block text-xs font-medium mb-1.5">{t("Country")}</label>
                      <Input id="contact-country" name="country" autoComplete="country-name" value={country} onChange={(e) => setCountry(e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="contact-industry" className="block text-xs font-medium mb-1.5">{t("Industry")}</label>
                      <Select value={industry} onValueChange={setIndustry}>
                        <SelectTrigger id="contact-industry" aria-label={t("Industry")}><SelectValue placeholder={t("Select industry")} /></SelectTrigger>
                        <SelectContent>
                          {industryOptions.map((opt) => (<SelectItem key={opt} value={opt}>{t(opt)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label htmlFor="contact-machines" className="block text-xs font-medium mb-1.5">{t("Machines / lines")}</label>
                      <Input id="contact-machines" name="machines" placeholder="e.g. 12" value={machines} onChange={(e) => setMachines(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-erp" className="block text-xs font-medium mb-1.5">{t("Current ERP / APS")}</label>
                    <Input id="contact-erp" name="erp" placeholder={t("SAP, Oracle, Dynamics, factory.online…")} value={erp} onChange={(e) => setErp(e.target.value)} />
                  </div>
                  <fieldset>
                    <legend className="block text-xs font-medium mb-2">{t("Main goals")}</legend>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                      {goalOptions.map((g) => (
                        <label key={g} className="flex items-center gap-2 text-xs cursor-pointer">
                          <Checkbox aria-label={t(g)} checked={goals.includes(g)} onCheckedChange={() => toggleGoal(g)} />
                          <span>{t(g)}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium mb-1.5">{t("Message")}</label>
                    <Textarea id="contact-message" name="message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("Tell us briefly about your factory and priorities…")} />
                  </div>
                  <Button className="w-full shadow-glow-primary" size="lg" type="submit" disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t(submitLabel)}
                    <ArrowRight className="ms-2 h-4 w-4" />
                  </Button>
                </form>
              </div>
            </AnimatedItem>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
